import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Clock, CheckCircle, AlertTriangle, FileText, Download, HelpCircle } from "lucide-react";
import { consumirApi } from "../api/ph";
import { jwtDecode } from "jwt-decode";
import Modal from "./Modal";
import { twMerge } from "tailwind-merge";
import { startTour, stopTour } from "@ipejal/tour-manager";
import "driver.js/dist/driver.css";

export default function Calendar({ onClose, folioPreregistro, onCitaConfirmada }) {
    const hoy = new Date();
    const [mesActual, setMesActual] = useState(hoy.getMonth());
    const [anioActual, setAnioActual] = useState(hoy.getFullYear());
    const [fechaSeleccionada, setFechaSeleccionada] = useState(hoy);
    const [citas, setCitas] = useState([]);
    const [requisitoDoc, setRequisitoDoc] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasLoaded, setHasLoaded] = useState(false);
    const [modalAgotado, setModalAgotado] = useState(false);
    const [mensajeAgotado, setMensajeAgotado] = useState("");
    const apiUrl_agendador = process.env.REACT_APP_URL_AGENDADOR;
    const idSede = localStorage.getItem("id_sede") || process.env.REACT_APP_ID_SEDE;
    const userStored = JSON.parse(localStorage.getItem("user") || "{}");
    const rawCodigo = userStored.username || "2023110589";
    const codigoAfiliado = (typeof rawCodigo === "string" && rawCodigo.startsWith("deudo-"))
        ? (rawCodigo.split("-")[1] || rawCodigo)
        : rawCodigo;
    const tourAttempted = useRef(false);

    useEffect(() => {
        return () => {
            stopTour();
        };
    }, []);

    const handleStartTour = (force = true) => {
        // Reset scroll position on mobile to prevent driver.js coordinate offset bugs
        const contentDiv = document.querySelector('.overflow-y-auto');
        if (contentDiv) {
            contentDiv.scrollTop = 0;
        }

        const steps = [];
        const isMobile = window.innerWidth < 768;

        steps.push({
            target: isMobile ? "modal-calendario-cita" : "cal-green-info",
            title: "Días Disponibles",
            description: "Los días marcados en verde en el calendario tienen horarios libres para agendar tu cita.",
            position: "bottom"
        });

        const greenBtn = document.querySelector('[data-tour="dia-disponible-btn"]');
        if (greenBtn || isMobile) {
            steps.push({
                target: isMobile ? "modal-calendario-cita" : "dia-disponible-btn",
                title: "Selecciona el Día",
                description: "Haz clic en cualquiera de los números verdes para ver sus horas libres.",
                position: isMobile ? "bottom" : "top"
            });
        }

        const hrsSec = document.querySelector('[data-tour="horarios-disponibles-section"]');
        if (hrsSec || isMobile) {
            steps.push({
                target: isMobile ? "modal-calendario-cita" : "horarios-disponibles-section",
                title: "Elige la Hora",
                description: "Aquí aparecerán las horas disponibles para el día que elegiste. Haz clic en la hora que prefieras.",
                position: isMobile ? "bottom" : "top"
            });
        }

        const tourJSON = {
            steps,
            options: {
                persistKey: "tour-calendar",
                showProgress: true,
                allowClose: true,
                doneBtnText: "Entendido",
                nextBtnText: "Siguiente Paso",
                prevBtnText: "Volver Atrás"
            }
        };

        try {
            startTour(tourJSON, force);
        } catch (error) {
            console.error("Error al iniciar el tour del calendario:", error);
        }
    };

    useEffect(() => {
        if (hasLoaded && !tourAttempted.current) {
            tourAttempted.current = true;
            if (window.innerWidth < 768) return; // No auto-iniciar en móviles

            const isCompleted = localStorage.getItem("tour-calendar");
            if (isCompleted !== "completed") {
                const timer = setTimeout(() => {
                    handleStartTour(false);
                }, 800);
                return () => clearTimeout(timer);
            }
        }
    }, [hasLoaded]);

    const meses = [
        "enero", "febrero", "marzo", "abril", "mayo", "junio",
        "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
    ];
    const diasSemana = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

    const diasEnMes = new Date(anioActual, mesActual + 1, 0).getDate();
    const primerDiaMes = new Date(anioActual, mesActual, 1).getDay();

    const handleMesAnterior = () => {
        if (mesActual === 0) {
            setMesActual(11);
            setAnioActual(anioActual - 1);
        } else {
            setMesActual(mesActual - 1);
        }
    };

    const handleMesSiguiente = () => {
        if (mesActual === 11) {
            setMesActual(0);
            setAnioActual(anioActual + 1);
        } else {
            setMesActual(mesActual + 1);
        }
    };

    const handleSeleccionarDia = (dia) => {
        const fechaActual = new Date(anioActual, mesActual, dia).toISOString().split("T")[0];
        const citaDelDia = citas.find((c) => c.fecha === fechaActual);

        if (!citaDelDia) {
            setMensajeAgotado("Lo sentimos, no hay citas programadas para este día. Por favor, selecciona una de las fechas marcadas en verde para agendar tu cita.");
            setModalAgotado(true);
        } else if (citaDelDia.tiene_disponibilidad === false || !citaDelDia.horas || citaDelDia.horas.length === 0) {
            setMensajeAgotado("Lo sentimos, los horarios para este día se encuentran agotados. Por favor, selecciona una de las fechas marcadas en verde para agendar tu cita.");
            setModalAgotado(true);
        }

        setFechaSeleccionada(new Date(anioActual, mesActual, dia));
        setHoraSeleccionada(null); // Resetear hora al cambiar de día
    };

    const getOrdinal = (n) => `${n}.º`;

    // 🔹 Cargar citas desde API
    useEffect(() => {
        const obtenerCitas = async () => {
            setLoading(true);
            const res = await consumirApi({
                url: `${apiUrl_agendador}/GetCitasDisponibles`,
                method: "POST",
                body: { id_sede: parseInt(idSede), id_catalogo_tramite: parseInt(localStorage.getItem("id_catalogo_tramite")) || 19 },
            });
            if (res.success) {
                setCitas(res.data?.citas || []);
            } else {
                setError(res.message || "Error al obtener citas");
            }

            // OBTENER REQUISITOS (DOCUMENTOS)
            const idTipoPrestamo = localStorage.getItem("id_tipo_prestamo");
            if (idTipoPrestamo) {
                const resDoc = await consumirApi({
                    url: `${process.env.REACT_APP_API_URL}/tipoPrestamosDocumentos/${idTipoPrestamo}`,
                    method: "GET"
                });
                if (resDoc.success && resDoc.data?.data?.length > 0) {
                    setRequisitoDoc(resDoc.data.data[0].documentos || []);
                }
            }

            setLoading(false);
            setHasLoaded(true);
        };

        obtenerCitas();
    }, []);

    const fechaISO = fechaSeleccionada.toISOString().split("T")[0];
    const citaDelDia = citas.find((c) => c.fecha === fechaISO);
    const tieneAlgunaDisponibilidad = Array.isArray(citas) && citas.length > 0 && citas.some((c) =>
        c.tiene_disponibilidad && Array.isArray(c.horas) && c.horas.length > 0
    );
    const botonAgendarRef = useRef(null);
    const horariosRef = useRef(null);
    const [horaSeleccionada, setHoraSeleccionada] = useState(null);

    // 🔹 Scroll al botón al seleccionar horario
    useEffect(() => {
        if (horaSeleccionada) {
            setTimeout(() => {
                botonAgendarRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
        }
    }, [horaSeleccionada]);

    // 🔹 Scroll a horarios al seleccionar fecha
    useEffect(() => {
        if (fechaSeleccionada && citaDelDia?.horas?.length > 0) {
            setTimeout(() => {
                horariosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        }
    }, [fechaSeleccionada, citaDelDia]);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [detalleRequisito, setDetalleRequisito] = useState(null);
    const abrirModal = (requisitos) => {
        setDetalleRequisito(Array.isArray(requisitos) ? requisitos : [requisitos]);
        setModalAbierto(true);
    };
    const [checkedItems, setCheckedItems] = useState({});

    // Limpiar los checkboxes cada vez que se cierre el modal
    useEffect(() => {
        if (!modalAbierto) {
            setCheckedItems({});
        }
    }, [modalAbierto]);

    const toggleCheck = (id) => {
        setCheckedItems((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const [confirmando, setConfirmando] = useState(false);
    const [modalExito, setModalExito] = useState(false);
    const [modalError, setModalError] = useState(false);
    const [modalAviso, setModalAviso] = useState(false);
    const [mensajeError, setMensajeError] = useState("");
    const [citaConfirmadaData, setCitaConfirmadaData] = useState(null);
    const [descargando, setDescargando] = useState(false);

    // 🔹 Restaurar reserva activa de localStorage al montar si coincide con este folio
    useEffect(() => {
        const reservaGuardada = localStorage.getItem("reserva_cita");
        if (reservaGuardada) {
            try {
                const parsed = JSON.parse(reservaGuardada);
                if (parsed.folioPreregistro === folioPreregistro) {
                    const ahora = Date.now();
                    if (parsed.expiration > ahora) {
                        if (parsed.horaSeleccionada) setHoraSeleccionada(parsed.horaSeleccionada);
                        if (parsed.fechaSeleccionada) setFechaSeleccionada(new Date(parsed.fechaSeleccionada));
                        if (parsed.requisitoDoc) setRequisitoDoc(parsed.requisitoDoc);

                        setDetalleRequisito(Array.isArray(parsed.requisitoDoc) ? parsed.requisitoDoc : [parsed.requisitoDoc]);
                        setModalAbierto(true);
                    }
                }
            } catch (e) {
                console.error("Error al restaurar reserva:", e);
            }
        }
    }, [folioPreregistro]);

    const handleConfirm = async () => {
        if (!horaSeleccionada?.id_cita) return;

        setConfirmando(true);
        try {
            const isDeudo = userStored?.userType?.toLowerCase() === "deudo";

            // Limpieza del nombre de beneficiario
            const rawNombre = userStored.displayName || `${userStored.firstName || ''} ${userStored.firstLastName || ''} ${userStored.secondLastName || ''}`;
            const nombreBeneficiario = rawNombre ? rawNombre.replace(/-/g, "").replace(/\s+/g, " ").trim() : "";
            const correoBeneficiario = (userStored.email || userStored.correo || "").trim();

            const urlReserva = isDeudo
                ? `${apiUrl_agendador}/ReservarCitaBeneficiario`
                : `${apiUrl_agendador}/ReservarCita`;

            const bodyReserva = isDeudo
                ? {
                    id_cita: horaSeleccionada.id_cita,
                    codigo_afiliado: String(codigoAfiliado),
                    nombre_beneficiario: nombreBeneficiario,
                    correo_beneficiario: correoBeneficiario
                }
                : {
                    id_cita: horaSeleccionada.id_cita,
                    codigo_afiliado: String(codigoAfiliado)
                };

            const res = await consumirApi({
                url: urlReserva,
                method: "POST",
                headers: { 'x-api-key': apiKeyCitas },
                body: bodyReserva
            });

            if (res.success) {
                localStorage.removeItem("reserva_cita");

                // --- NUEVO: Obtener estrictamente el folio retornado por el API ---
                const folioReal = res.data?.folio;
                const fechaCitaFormat = `${fechaISO}T${horaSeleccionada.hora}`;

                // --- NUEVO: Consumir API cita-usuario ---
                let userEmail = "";
                try {
                    const token = localStorage.getItem("token");
                    if (token) {

                        const decoded = jwtDecode(token);
                        userEmail = decoded.email || decoded.username || decoded.correo || userStored?.email || userStored?.correo || decoded.sub || "";

                        const tipoPrestamoId = parseInt(localStorage.getItem("id_tipo_prestamo")) || 0;

                        const bodyCitaUsuario = {
                            "correo_electronico": userEmail,
                            "usuario_codigo": String(codigoAfiliado),
                            "folio_cita": String(folioReal),
                            "folio_prevalidacion": parseInt(folioPreregistro),
                            "fecha_cita": fechaCitaFormat,
                            "id_estado": 1,
                            "id_tipo_prestamo": tipoPrestamoId
                        };

                        const resUsuario = await consumirApi({
                            url: `${process.env.REACT_APP_API_URL}/cita-usuario/`,
                            method: "POST",
                            body: bodyCitaUsuario
                        });

                        if (resUsuario.success) {
                        } else {
                            console.error("❌ Error al guardar Cita-Usuario:", resUsuario);
                            // Opcional: ¿Mostrar error al usuario o dejar pasar pq la cita ya se reservó en sist. agendador?
                        }
                    }
                } catch (err) {
                    console.error("Error procesando cita-usuario:", err);
                }
                // ----------------------------------------

                // Guardar datos para el modal de éxito
                setCitaConfirmadaData({
                    fecha: fechaSeleccionada.toLocaleDateString(),
                    hora: horaSeleccionada.hora.slice(0, 5),
                    folio: folioReal || "Pendiente",
                    email: userEmail,
                    folioReal: folioReal,
                    fechaCitaFormat: fechaCitaFormat
                });

                setModalAbierto(false); // Cierra modal de requisitos
                setModalExito(true); // Abre modal de éxito
            } else {
                setMensajeError(res.message || "Error al confirmar la cita");
                setModalError(true);
            }
        } catch (error) {
            console.error("Error al confirmar cita:", error);
            setMensajeError("Ocurrió un error al confirmar la cita.");
            setModalError(true);
        } finally {
            setConfirmando(false);
        }
    };

    const handleCerrarExito = () => {
        if (typeof onCitaConfirmada === "function" && citaConfirmadaData?.folioReal) {
            onCitaConfirmada(citaConfirmadaData.folioReal, citaConfirmadaData.fechaCitaFormat);
        }
        setModalExito(false);
        onClose(); // Cerrar el calendario completo
    };

    const handleDescargarDocumento = async () => {
        const { folio } = citaConfirmadaData || {};
        if (!folio) {
            setMensajeError("No se tienen datos suficientes para descargar el comprobante.");
            setModalError(true);
            return;
        }

        setDescargando(true);
        // URL construida desde el env (usando puerto 3001 del agendador)
        const url = `${apiUrl_agendador}/GenerarPDF`;

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    "codigo_afiliado": codigoAfiliado, // Quemado según instrucciones
                    "folio_cita": folio
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error respuesta PDF:", errorText);
                throw new Error(`Error al generar el PDF: ${response.statusText}`);
            }

            // Asumimos que es un archivo (blob)
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `Comprobante_Cita_${folio}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
            }, 1000);

        } catch (error) {
            console.error("Error en descarga:", error);
            setMensajeError("Hubo un error al intentar generar el comprobante.");
            setModalError(true);
        } finally {
            setDescargando(false);
        }
    };

    const [reservando, setReservando] = useState(false);
    const apiKeyCitas = process.env.REACT_APP_API_KEY_CITAS;
    const tiempoReservaMinutos = parseInt(process.env.REACT_APP_TIEMPO_RESERVA_MINUTOS) || 5;

    const [timeLeft, setTimeLeft] = useState(null);

    // 🔹 Lógica de Temporizador y Cancelación Automática
    useEffect(() => {
        const actualizarTemporizador = async () => {
            const reservaGuardada = localStorage.getItem("reserva_cita");
            if (!reservaGuardada) {
                setTimeLeft(null);
                return;
            }

            const { expiration, id_cita } = JSON.parse(reservaGuardada);
            const ahora = Date.now();
            const diferencia = expiration - ahora;

            if (diferencia <= 0) {
                await cancelarCita(id_cita);
            } else {
                setTimeLeft(Math.ceil(diferencia / 1000));
            }
        };

        const intervalo = setInterval(actualizarTemporizador, 1000); // Check every second
        actualizarTemporizador(); // Check immediately

        return () => clearInterval(intervalo);
    }, []);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const cancelarCita = async (idCita) => {
        try {
            await consumirApi({
                url: `${apiUrl_agendador}/ApartarCita`,
                method: "POST",
                headers: { 'x-api-key': apiKeyCitas },
                body: {
                    id_cita: idCita,
                    accion: false,
                    codigo_afiliado: codigoAfiliado
                }
            });
        } catch (error) {
            console.error("Error al cancelar la cita automáticamente:", error);
        } finally {
            localStorage.removeItem("reserva_cita");
            window.location.reload(); // Recargar la página
        }
    };

    const handleApartarCita = async () => {
        if (!horaSeleccionada?.id_cita) return;

        setReservando(true);
        try {
            const res = await consumirApi({
                url: `${apiUrl_agendador}/ApartarCita`,
                method: "POST",
                headers: {
                    'x-api-key': apiKeyCitas
                },
                body: {
                    id_cita: horaSeleccionada.id_cita,
                    accion: true,
                    codigo_afiliado: codigoAfiliado
                }
            });

            if (res.success) {
                const expirationTime = Date.now() + tiempoReservaMinutos * 60 * 1000;
                localStorage.setItem("reserva_cita", JSON.stringify({
                    id_cita: horaSeleccionada.id_cita,
                    expiration: expirationTime,
                    folioPreregistro: folioPreregistro,
                    horaSeleccionada: horaSeleccionada,
                    fechaSeleccionada: fechaSeleccionada,
                    requisitoDoc: requisitoDoc
                }));
                abrirModal(requisitoDoc);
            } else {
                let mensajeAMostrar = res.message || "No se pudo apartar la cita. Intente de nuevo.";
                if (mensajeAMostrar.includes("La cita ya está apartada por ti")) {
                    mensajeAMostrar = "La cita ya está apartada por ti. Esta cita se liberará durante el día.";
                    setMensajeError(mensajeAMostrar);
                    setModalAviso(true);
                } else {
                    setMensajeError(mensajeAMostrar);
                    setModalError(true);
                }
            }
        } catch (error) {
            console.error("Error al apartar cita:", error);
            setMensajeError("Ocurrió un error al intentar apartar la cita.");
            setModalError(true);
        } finally {
            setReservando(false);
        }
    };

    return (
        <>
            {/* Main Calendar Modal */}
            <Modal
                isOpen={true}
                onClose={onClose}
                title="Agenda tu cita"
                fullScreen={true}
            >
                <div id="modal-calendario-cita" data-tour="modal-calendario-cita" className="max-w-6xl mx-auto w-full ">
                    <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start select-none">
                        {/* Left Side: Selected Date Card */}
                        <div className="w-full md:w-64 shrink-0">
                            <div
                                className="bg-white rounded-md shadow-lg border border-slate-100 overflow-hidden flex flex-row md:flex-col items-center md:items-center bg-gradient-to-br from-white to-slate-50/50"
                            >
                                <div className="bg-slate-50 md:bg-primary text-black md:text-white w-24 md:w-full h-full md:h-auto flex items-center justify-center py-4 font-bold uppercase tracking-wider text-[10px] md:text-sm border-r border-slate-100 md:border-none">
                                    {meses[fechaSeleccionada.getMonth()]}
                                </div>
                                <div className="flex-1 py-4 md:py-8 flex items-center justify-center">
                                    <span
                                        className="text-4xl md:text-7xl font-bold text-[#1a1a1a]"
                                    >
                                        {fechaSeleccionada.getDate()}
                                    </span>
                                </div>
                                <div className="hidden md:block pb-6 text-slate-300 font-bold text-lg">
                                    {fechaSeleccionada.getFullYear()}
                                </div>
                            </div>

                            <div className="mt-4 md:mt-6 space-y-2 md:space-y-3 px-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                        <div className="size-2 rounded-full bg-green-500"></div>
                                        <span>Disponibilidad</span>
                                    </div>
                                    <button
                                        onClick={handleStartTour}
                                        className="flex items-center justify-center gap-1 px-2.5 py-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-primary font-black text-[9px] uppercase tracking-wider shadow-sm active:scale-95 transition-all"
                                    >
                                        <HelpCircle className="w-3 h-3" />
                                        <span>Guía</span>
                                    </button>
                                </div>
                                <p data-tour="cal-green-info" className="text-xs text-gray-500 leading-relaxed font-medium">
                                    Los días marcados en <span className="text-green-600 font-bold">verde</span> son los días que tienen horarios disponibles para agendar tu cita. Selecciona uno para elegir tu hora.
                                </p>

                                <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest pt-2 border-t border-slate-100">
                                    <div className="size-2 rounded-full bg-red-500"></div>
                                    <span>Sin Horarios</span>
                                </div>
                                <p className="text-xs text-gray-500 leading-relaxed font-medium">
                                    Los días marcados en <span className="text-red-600 font-bold">rojo</span> no tienen horarios disponibles.
                                </p>
                            </div>
                        </div>

                        {/* Right Side: Calendar Grid & Hours */}
                        <div className="flex-1 w-full space-y-8">
                            {/* Month Picker */}
                            <div className="flex items-center justify-between bg-slate-50 p-1.5 rounded-md">
                                <button
                                    onClick={handleMesAnterior}
                                    className="p-2.5 hover:bg-white hover:shadow-sm rounded-xl"
                                >
                                    <ChevronLeft className="w-5 h-5 text-primary" />
                                </button>
                                <div className="font-bold text-[#1a1a1a] capitalize text-lg tracking-tight">
                                    {meses[mesActual]} {anioActual}
                                </div>
                                <button
                                    onClick={handleMesSiguiente}
                                    className="p-2.5 hover:bg-white hover:shadow-sm rounded-xl"
                                >
                                    <ChevronRight className="w-5 h-5 text-primary" />
                                </button>
                            </div>

                            {!loading && !tieneAlgunaDisponibilidad && (
                                <div className="bg-slate-100 border border-slate-200/60 p-4 rounded-xl flex items-start gap-3 mb-6">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
                                    <div>
                                        <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-1">Sin Citas Disponibles</h4>
                                        <p className="text-slate-500 font-medium text-xs leading-relaxed">
                                            No hay horarios disponibles para agendar citas en el periodo actual. Por favor, intenta consultar en otro mes o comunícate con la sede para más información.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Calendar Grid */}
                            <div>
                                <div className="grid grid-cols-7 mb-4">
                                    {diasSemana.map((d) => (
                                        <div key={d} className="text-center text-[10px] font-black text-gray-700 uppercase tracking-widest">
                                            {d}
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-7 gap-1 md:gap-2">
                                    {Array(primerDiaMes).fill(null).map((_, i) => <div key={`vacio-${i}`} />)}
                                    {Array.from({ length: diasEnMes }, (_, i) => i + 1).map((dia) => {
                                        const fechaActual = new Date(anioActual, mesActual, dia).toISOString().split("T")[0];
                                        const citaDelDia = citas.find((c) => c.fecha === fechaActual);
                                        const tieneCitas = !!citaDelDia;
                                        const tieneDisponibilidad = tieneCitas &&
                                            (citaDelDia.tiene_disponibilidad !== false) &&
                                            (Array.isArray(citaDelDia.horas) && citaDelDia.horas.length > 0);

                                        const esSeleccionado =
                                            fechaSeleccionada.getDate() === dia &&
                                            fechaSeleccionada.getMonth() === mesActual &&
                                            fechaSeleccionada.getFullYear() === anioActual;

                                        return (
                                            <button
                                                key={dia}
                                                onClick={() => handleSeleccionarDia(dia)}
                                                data-tour={tieneDisponibilidad ? "dia-disponible-btn" : undefined}
                                                className={twMerge(
                                                    "aspect-square flex items-center justify-center rounded-md text-[13px] md:text-sm font-bold relative group",
                                                    esSeleccionado
                                                        ? (tieneCitas && !tieneDisponibilidad)
                                                            ? "bg-red-600 text-white shadow-lg shadow-red-600/30 z-10"
                                                            : "bg-primary text-white shadow-lg shadow-primary/30 z-10"
                                                        : tieneCitas
                                                            ? tieneDisponibilidad
                                                                ? "bg-green-50 text-green-700 hover:bg-green-100"
                                                                : "bg-red-50 text-slate-800 hover:bg-red-100 border border-red-200/30"
                                                            : "text-gray-400 hover:bg-gray-50 border border-transparent"
                                                )}
                                            >
                                                {dia}
                                                {tieneCitas && !esSeleccionado && (
                                                    <div className={twMerge(
                                                        "absolute bottom-1 size-1 rounded-full",
                                                        tieneDisponibilidad ? "bg-green-500" : "bg-red-500"
                                                    )}></div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Time Slots Area */}
                            <div ref={horariosRef} data-tour="horarios-disponibles-section" className="border-t border-slate-100 pt-6 mt-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-bold text-[#1a1a1a] text-base tracking-tight">Horarios Disponibles</h3>
                                    {fechaSeleccionada && (
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">
                                            {fechaSeleccionada.toLocaleDateString()}
                                        </span>
                                    )}
                                </div>

                                {loading ? (
                                    <div className="py-12 text-center text-gray-400 font-bold italic">Cargando espacios...</div>
                                ) : citaDelDia?.horas?.length > 0 ? (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                        {citaDelDia.horas.map((h) => {
                                            const esSeleccionada = horaSeleccionada?.id_cita === h.id_cita;
                                            return (
                                                <button
                                                    key={h.id_cita}
                                                    onClick={() => setHoraSeleccionada(h)}
                                                    className={twMerge(
                                                        "py-3 rounded-md font-bold text-sm border-2",
                                                        esSeleccionada
                                                            ? "bg-primary border-primary text-white shadow-lg shadow-primary/20"
                                                            : "bg-white border-gray-100 text-gray-600 hover:border-primary/30"
                                                    )}
                                                >
                                                    {h.hora.slice(0, 5)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="py-12 text-center bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-100">
                                        <Clock className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                        <p className="text-gray-400 font-bold text-sm">No hay horarios para este día</p>
                                    </div>
                                )}

                                {/* Action Button Area - Stable Height */}
                                <div ref={botonAgendarRef} className="mt-8 min-h-[72px]">
                                    {horaSeleccionada && (
                                        <button
                                            onClick={handleApartarCita}
                                            disabled={reservando}
                                            className="w-full py-4 bg-primary text-white rounded-md font-bold text-base shadow-lg shadow-primary/20 hover:bg-primary/95 transition-all disabled:opacity-50"
                                        >
                                            {reservando ? "PROCESANDO..." : "CONTINUAR CON LA CITA"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Requirements Checklist Modal */}
            <Modal
                isOpen={modalAbierto}
                onClose={() => { }} // No permitir cierre por backdrop
                title="Declaración de Requisitos"
                maxWidth="max-w-2xl"
                showClose={false} // No mostrar el botón X
            >

                <div className="space-y-6">
                    {timeLeft !== null && (
                        <div className="bg-primary/5 text-primary p-4 rounded-3xl flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Clock className="w-5 h-5 animate-pulse" />
                                <span className="font-black text-sm uppercase tracking-widest">Tiempo de Reserva</span>
                            </div>
                            <span className="text-2xl font-black tabular-nums">{formatTime(timeLeft)}</span>
                        </div>
                    )}

                    <div className="p-6 bg-white border border-primary/10 rounded-[2rem] flex gap-4">
                        <div className="size-10 shrink-0 bg-primary/5 rounded-full flex items-center justify-center text-primary">
                            <AlertTriangle className="w-5 h-5" />
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed font-bold">
                            Al confirmar, usted declara que los documentos originales son los mismos que presentó en la etapa de pre-validación digital.
                        </p>
                    </div>

                    <div className="space-y-3">
                        {detalleRequisito?.map((r) => (
                            <button
                                key={r.tipo_documento_id}
                                onClick={() => toggleCheck(r.tipo_documento_id)}
                                className={twMerge(
                                    "w-full flex items-center justify-between p-5 rounded-[1.5rem] border-2 transition-all text-left",
                                    checkedItems[r.tipo_documento_id]
                                        ? "bg-green-50 border-green-500 shadow-sm"
                                        : "bg-white border-gray-100 hover:border-primary/20"
                                )}
                            >
                                <span className="text-gray-900 font-bold text-sm pr-4 line-clamp-2">
                                    {r.nombre_tipo_documento}
                                </span>
                                <div className={twMerge(
                                    "size-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-all",
                                    checkedItems[r.tipo_documento_id] ? "bg-green-500 border-green-500 text-white" : "bg-white border-gray-200"
                                )}>
                                    {checkedItems[r.tipo_documento_id] && <CheckCircle className="w-4 h-4" />}
                                </div>
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={handleConfirm}
                        disabled={confirmando || Object.keys(checkedItems).length !== detalleRequisito?.length}
                        className="w-full py-5 bg-green-600 text-white rounded-[2rem] font-black text-lg shadow-2xl shadow-green-600/20 hover:bg-green-700 transition-all disabled:opacity-20 disabled:grayscale"
                    >
                        {confirmando ? "CONFIRMANDO..." : "FINALIZAR AGENDADO"}
                    </button>
                </div>
            </Modal>

            {/* Success Modal */}
            <Modal
                isOpen={modalExito}
                onClose={handleCerrarExito}
                title="¡Cita Agendada!"
                maxWidth="max-w-md"
                showClose={false}
            >
                <div className="text-center py-4">
                    <div className="size-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                        <CheckCircle className="w-12 h-12" />
                    </div>

                    <h2 className="text-2xl font-black text-[#1a1a1a] mb-2 tracking-tight">Confirmado con Éxito</h2>
                    <p className="text-gray-500 font-bold mb-8">
                        Tu cita el <span className="text-primary">{citaConfirmadaData?.fecha}</span> a las <span className="text-primary">{citaConfirmadaData?.hora}</span> ha sido reservada.
                    </p>

                    <div className="bg-gray-50 rounded-[2rem] p-6 mb-8 border border-gray-100">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Folio de cita</span>
                        <span className="text-3xl font-black text-green-700 tracking-widest tabular-nums">
                            {citaConfirmadaData?.folio}
                        </span>
                    </div>

                    <div className="grid gap-4">
                        <button
                            onClick={handleDescargarDocumento}
                            disabled={descargando}
                            className="w-full py-4 bg-primary/5 text-primary rounded-[1.5rem] font-black flex items-center justify-center gap-3 hover:bg-primary/10 transition-all disabled:opacity-50"
                        >
                            {descargando ? (
                                <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <Download className="w-5 h-5" />
                            )}
                            DESCARGAR COMPROBANTE
                        </button>
                        <button
                            onClick={handleCerrarExito}
                            className="w-full py-4 bg-primary text-white rounded-[1.5rem] font-black shadow-xl shadow-primary/20 hover:bg-primary/95 transition-all"
                        >
                            CERRAR Y FINALIZAR
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Error Modal */}
            <Modal
                isOpen={modalError}
                onClose={() => setModalError(false)}
                title="Error"
                maxWidth="max-w-sm"
            >
                <div className="text-center py-4">
                    <div className="size-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 text-red-600">
                        <AlertTriangle className="w-10 h-10" />
                    </div>
                    <h3 className="text-xl font-black text-[#1a1a1a] mb-2 uppercase tracking-tight">VUELVE A INTENTARLO</h3>
                    <p className="text-gray-500 font-bold mb-8 italic">{mensajeError}</p>
                    <button
                        onClick={() => setModalError(false)}
                        className="w-full py-4 bg-gray-100 text-gray-600 rounded-md font-black hover:bg-gray-200 transition-all"
                    >
                        REGRESAR
                    </button>
                </div>
            </Modal>

            {/* Aviso Modal (Cita Apartada) */}
            <Modal
                isOpen={modalAviso}
                onClose={() => setModalAviso(false)}
                title="Aviso"
                maxWidth="max-w-md"
            >
                <div className="text-center py-4">
                    <div className="size-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-600">
                        <Clock className="w-10 h-10" />
                    </div>
                    <h3 className="text-xl font-black text-[#1a1a1a] mb-2 uppercase tracking-tight">Cita ya apartada</h3>
                    <div className="bg-amber-50/50 p-6 rounded-md border border-amber-100 mb-8">
                        <p className="text-gray-600 font-bold leading-relaxed">{mensajeError}</p>
                    </div>
                    <button
                        onClick={() => setModalAviso(false)}
                        className="w-full py-4 bg-primary text-white rounded-md font-black shadow-lg shadow-primary/20 hover:bg-primary/95 transition-all"
                    >
                        ENTENDIDO
                    </button>
                </div>
            </Modal>

            {/* Agotado Modal */}
            <Modal
                isOpen={modalAgotado}
                onClose={() => setModalAgotado(false)}
                title="Sin horarios disponibles"
                maxWidth="max-w-md"
            >
                <div className="text-center py-6">
                    <div className="size-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                        <AlertTriangle className="w-10 h-10" />
                    </div>
                    <h3 className="text-xl font-black text-[#1a1a1a] mb-2 uppercase tracking-tight">Citas Agotadas</h3>
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-8">
                        <p className="text-gray-600 font-bold leading-relaxed text-sm">
                            {mensajeAgotado}
                        </p>
                    </div>
                    <button
                        onClick={() => setModalAgotado(false)}
                        className="w-full py-4 bg-primary text-white rounded-xl font-black shadow-lg shadow-primary/20 hover:bg-primary/95 transition-all"
                    >
                        ENTENDIDO
                    </button>
                </div>
            </Modal>
        </>
    );
}
